import { Link } from 'react-router-dom';
import { Building2, CheckCircle2, FileText, Receipt, RotateCcw, Users } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { useContractImportStore } from '@/store/useContractImportStore';

const LINK_CLASS =
  'flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors';

export function ResultStep() {
  const { t } = useTranslation('contractImport');
  const { result, fileName, reset } = useContractImportStore();

  if (!result) return null;

  return (
    <Card>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-[#00A871]" strokeWidth={2} />
          <div>
            <h2 className="text-[15px] font-semibold tracking-tight text-slate-900">{t('result.title')}</h2>
            <p className="text-[12px] text-slate-500">{t('result.hint')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {result.customerId && (
            <Link to={`/customers/${result.customerId}`} className={LINK_CLASS}>
              <Users className="w-4 h-4 text-slate-400" strokeWidth={2} />
              {t('result.customer')}
            </Link>
          )}
          {result.objectId && (
            <Link to={`/objects/${result.objectId}`} className={LINK_CLASS}>
              <Building2 className="w-4 h-4 text-slate-400" strokeWidth={2} />
              {t('result.object')}
            </Link>
          )}
          {result.offerId && (
            <Link to={`/offers/${result.offerId}`} className={LINK_CLASS}>
              <FileText className="w-4 h-4 text-slate-400" strokeWidth={2} />
              {t('result.offer')}
            </Link>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1 text-[12px] text-slate-600">
          <div className="tabular-nums">{t('result.services', { count: result.serviceIds?.length ?? 0 })}</div>
          <div className="tabular-nums">{t('result.tasks', { count: result.taskIds?.length ?? 0 })}</div>
          <div>{t('result.documentStored', { fileName: fileName ?? '' })}</div>
        </div>

        <div className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-[12px] text-sky-800">
          <Receipt className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
          <span>
            {result.invoiceDraftCreated ? t('result.invoiceCreated') : t('result.invoiceExisting')}
          </span>
        </div>

        <div className="flex justify-end">
          <Button
            variant="secondary"
            leftIcon={<RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />}
            onClick={reset}
          >
            {t('actions.restart')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default ResultStep;
